<template>
  <div class="popup-form record-picker-popup">
    <div class="content-form-popup record-picker-content">
      <div class="header-text">
        <modal-close-button aria-label="Zavrieť príjmy" @click="closeDialog" />
        <div class="_900 big">Príjmy</div>
      </div>

      <div v-if="clientInfo" class="header-block inline">
        <div class="bold">{{ clientInfo.type }}</div>
        <img
          :src="getIcon(clientInfo.type)"
          loading="lazy"
          alt=""
          class="image-10"
        />
      </div>

      <record-picker-tabs
        v-model="activeSection"
        :registered-count="incomesGetter.length"
        aria-label="Sekcie príjmov"
      />

      <div class="w-form">
        <div id="email-form" class="form-2">
          <section v-if="activeSection === 'registered'" class="picker-section">
            <div class="section-intro">
              <div class="section-intro-title">Evidované príjmy</div>
              <div class="section-intro-copy">
                Prepínač určuje, ktoré príjmy sa započítajú do limitov a bankových ponúk.
              </div>
            </div>

            <div
              v-for="income in incomesGetter"
              :key="income.id"
              class="record-card"
              :class="{excluded: income.is_active === false}"
            >
              <div class="record-main">
                <div class="record-name">
                  <span v-if="income.zamestnavatel">
                    {{ income.zamestnavatel.name }}
                    <span class="record-type">({{ translateIncomeType(income.druh) }})</span>
                  </span>
                  <span v-else>{{ translateIncomeType(income.druh) }}</span>
                </div>
                <v-progress-circular
                  v-if="income.loading && togglingIncomeId !== income.id"
                  class="loading-spinner extrasmall"
                  indeterminate
                ></v-progress-circular>
                <div v-else class="record-amount">
                  +{{ incomeAmount(income) | dotTheAmount }} EUR
                </div>
              </div>

              <div v-if="income.error" class="record-warning">
                Príjem treba dokončiť, aby sa dal správne vypočítať.
              </div>

              <div class="record-actions">
                <button type="button" class="record-edit" @click="edit(income)">
                  <app-icon name="edit" :size="17" />
                  {{ income.error ? "Dokončiť" : "Upraviť" }}
                </button>
                <calculation-toggle
                  :value="income.is_active !== false"
                  :busy="togglingIncomeId === income.id"
                  @change="toggleIncome(income, $event)"
                />
              </div>
            </div>

            <div v-if="incomesGetter.length === 0" class="empty-records">
              <div class="empty-records-icon"><app-icon name="plus" :size="22" /></div>
              <div class="empty-records-title">Zatiaľ tu nie je žiadny príjem</div>
              <div class="empty-records-copy">Pridajte prvý príjem klienta a aplikácia ho zahrnie do výpočtov.</div>
              <button type="button" class="empty-records-action" @click="activeSection = 'new'">
                Pridať príjem
              </button>
            </div>
          </section>

          <section v-else class="picker-section new-record-section">
            <div class="splitter">
              1. Vyberte typ <strong>hlavného príjmu</strong>
            </div>
            <button type="button" class="option-block record-option" @click="incomeTypePicker('trvaly_pracovny_pomer')">
              <span class="bold">zo zamestnania</span>
              <app-icon name="plus" :size="19" />
            </button>
            <button type="button" class="option-block record-option" @click="incomeTypePicker('prijem_zivnostnika')">
              <span class="bold">zo živnosti</span>
              <app-icon name="plus" :size="19" />
            </button>
            <button type="button" class="option-block record-option" @click="incomeTypePicker('prijem_z_sro')">
              <span class="bold">z podnikania SRO</span>
              <app-icon name="plus" :size="19" />
            </button>

            <div class="splitter">
              2. Vyberte typ <strong>vedľajšieho príjmu</strong>
            </div>
            <button type="button" class="option-block record-option" @click="incomeTypePicker('prijem_z_prenajmu_nehnutelnosti')">
              <span class="bold">príjem z prenájmu</span>
              <app-icon name="plus" :size="19" />
            </button>
            <button type="button" class="option-block record-option" @click="incomeTypePicker('vysluhovy_dochodca')">
              <span class="bold">z dôchodku</span>
              <app-icon name="plus" :size="19" />
            </button>
            <button type="button" class="option-block record-option" @click="incomeTypePicker('materska_rodicovsky_prispevok')">
              <span class="bold">rodičovský príspevok</span>
              <app-icon name="plus" :size="19" />
            </button>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import translateFromDBValue from "../../../../../mixins/translateFromDBValue";
import AppIcon from "@/components/shared/AppIcon.vue";
import CalculationToggle from "@/components/shared/CalculationToggle.vue";
import ModalCloseButton from "@/components/shared/ModalCloseButton.vue";
import RecordPickerTabs from "@/components/shared/RecordPickerTabs.vue";

export default {
  name: "ClientIncomePicker",
  components: {AppIcon, CalculationToggle, ModalCloseButton, RecordPickerTabs},
  mixins: [translateFromDBValue],
  data: () => ({
    activeSection: "registered",
    clientInfo: null,
    client: null,
    togglingIncomeId: null,
  }),
  computed: {
    incomesGetter () {
      if (!this.clientInfo) return [];
      return this.$store.getters["mortgageDetailState/getClientIncomes"](this.clientInfo.type_code);
    },
  },
  watch: {
    'incomesGetter.length'(count, previous) {
      if (count > previous) this.activeSection = 'registered';
    },
  },
  created () {
    this.clientInfo = this.$store.getters["popupManagerState/getClientInfo"];
    this.activeSection = this.incomesGetter.length > 0 ? "registered" : "new";
  },
  methods: {
    getIcon (type) {
      if (type === "Žiadateľ") return require("@/images/avatar_applicant.png");
      if (type === "Spolužiadateľ") return require("@/images/avatar_coapplicant.png");
      return "";
    },
    incomeAmount (income) {
      return income.dti && income.dti.value != null ? income.dti.value : 0;
    },
    async toggleIncome (income, isActive) {
      if (this.togglingIncomeId !== null) return;
      this.togglingIncomeId = income.id;
      try {
        await this.$store.dispatch("mortgageDetailState/toggleClientIncome", {
          id: income.id,
          clientLabel: this.clientInfo.type,
          clientRole: this.clientInfo.type_code,
          isActive,
        });
      } finally {
        this.togglingIncomeId = null;
      }
    },
    edit (income) {
      this.incomeTypePicker(income.druh, income);
    },
    incomeTypePicker (incomeType, incomeData = null) {
      this.$store.commit("popupManagerState/pushToPopupOrder", {
        type: incomeType,
        payload: incomeData,
        component: "ClientIncomePopUp",
        rules: null,
      });
    },
    closeDialog () {
      this.$store.commit("popupManagerState/popItemFromPopupOrder");
    },
  },
};
</script>

<style lang="scss" scoped>
.record-picker-popup,
.record-picker-content {
  overflow-x: hidden;
}

.record-picker-content,
.record-picker-content > * {
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
}

.picker-section {
  display: grid;
  gap: 12px;
}

.section-intro {
  margin-bottom: 4px;
}

.section-intro-title {
  color: var(--app-text);
  font-size: 16px;
  font-weight: 900;
}

.section-intro-copy {
  margin-top: 3px;
  color: var(--app-text-muted);
  font-size: 12px;
  line-height: 1.45;
}

.record-card {
  display: grid;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--app-border);
  border-radius: 18px;
  background: var(--app-surface);
  box-shadow: var(--app-shadow);
  transition: border-color .18s ease, opacity .18s ease;
}

.record-card.excluded {
  border-style: dashed;
  background: color-mix(in srgb, var(--app-surface-muted) 82%, transparent);
}

.record-card.excluded .record-main {
  opacity: .65;
}

.record-main {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.record-name {
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--app-text);
  font-weight: 800;
  line-height: 1.35;
}

.record-type {
  color: var(--app-text-muted);
  font-weight: 600;
}

.record-amount {
  flex: 0 0 auto;
  color: var(--app-text);
  font-weight: 900;
  white-space: nowrap;
}

.record-warning {
  padding: 9px 11px;
  border-radius: 10px;
  background: color-mix(in srgb, #f5a524 14%, transparent);
  color: var(--app-text);
  font-size: 11px;
  line-height: 1.4;
}

.record-actions {
  display: grid;
  grid-template-columns: minmax(104px, .36fr) minmax(0, 1fr);
  gap: 8px;
  align-items: stretch;
}

.record-edit,
.empty-records-action {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: var(--app-surface-muted);
  color: var(--app-primary);
  font-weight: 800;
  cursor: pointer;
}

.record-edit:hover,
.record-edit:focus-visible,
.empty-records-action:hover,
.empty-records-action:focus-visible {
  border-color: var(--app-primary);
  outline: none;
}

.empty-records {
  display: grid;
  padding: 30px 20px;
  justify-items: center;
  gap: 8px;
  border: 1px dashed var(--app-border);
  border-radius: 18px;
  background: var(--app-surface-muted);
  text-align: center;
}

.empty-records-icon {
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 13px;
  background: var(--app-primary);
  color: #fff;
}

.empty-records-title {
  color: var(--app-text);
  font-weight: 900;
}

.empty-records-copy {
  max-width: 310px;
  color: var(--app-text-muted);
  font-size: 12px;
  line-height: 1.45;
}

.empty-records-action {
  margin-top: 8px;
  padding: 0 18px;
  background: var(--app-primary);
  color: #fff;
}

.record-option {
  min-width: 0;
  margin-right: 0;
  border: 1px solid var(--app-border);
  color: var(--app-text);
  text-align: left;
}

.record-option:hover,
.record-option:focus-visible {
  border-color: var(--app-primary);
  outline: none;
  color: var(--app-primary);
}

.popup {
  width: 35%;
  position: absolute !important;
  top: 0;
  overflow-y: auto;
  transition-duration: .5s;
}

@media (max-width: 480px) {
  .record-actions {
    grid-template-columns: 1fr;
  }
}
</style>
